import ValidateModule from "./ValidateModule";

import type { IEventChannel, ApiOpts } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";
import type { Criteria } from "../model/Criteria";
import type { SortOrder } from "react-md";

const es = global.es;

/**
 * @class WorkspaceModule
 */
export default class WorkspaceModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.get = this.get.bind(this);
        this.getByIds = this.getByIds.bind(this);
        this.getGeoMaps = this.getGeoMaps.bind(this);
        this.list = this.list.bind(this);
        this.listShared = this.listShared.bind(this);
        this.listOwned = this.listOwned.bind(this);
        this.count = this.count.bind(this);
        this.countOwned = this.countOwned.bind(this);
        this.countShared = this.countShared.bind(this);
        this.create = this.create.bind(this);
        this.createWithoutGeoMap = this.createWithoutGeoMap.bind(this);
        this.update = this.update.bind(this);
        this.remove = this.remove.bind(this);
        this.removeBulk = this.removeBulk.bind(this);
        this.shareWorkspace = this.shareWorkspace.bind(this);
    }

    /**
     * Get the resource from the server
     * 
     * @param id 
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    get(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param ids
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    getByIds(ids: number[], opts?: ApiOpts) {
        if (!ValidateModule.checkIds(ids)) {
            return es.util.deferred.rejected();
        }
        
        let url = es.api.getURL(this.resourceName) + "/ids?ids=" + es.util.url.stringify(ids);
        
        if (url.length > this.rqBroker.MAX_URL_LENGTH) {
            url = es.api.getURL(this.resourceName) + "/ids_bulk";

            return this.rqBroker.doPost({url: url, data : ids, opts: opts});
        }

        return this.cache.doGet(this.rqBroker, this.resourceName, url, ids, opts);
    }
    
    /**
     * Retrieve all GeoMaps belonging to a workspace
     * 
     * @param ids
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    getGeoMaps(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        var url = es.api.getURL(this.resourceName) + "/" + id + "/geomaps";
        return this.rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * Get the resource from the server
     * 
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    list(criteria?: Criteria, sortOrder?: SortOrder, offset?: number, length?: number, usedAfter?: number, opts?: ApiOpts) {
        //return rqBroker.doGet({url: es.api.getListQueryURL(resourceName, criteria, sortOrder, offset, length)});
        return this.cache.doGetList(this.rqBroker, this.resourceName, criteria, sortOrder, offset, length, usedAfter, opts);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    listShared(criteria?: Criteria, sortOrder?: SortOrder, offset?: number, length?: number, usedAfter?: number, opts?: ApiOpts) {
        return this.cache.doGetList(this.rqBroker, this.resourceName + "/listshared", criteria, sortOrder, offset, length, usedAfter, opts);
    }

    /**
     * Get the resource from the server
     * 
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * @param usedAfter time in miliseconds to list only objects used after given timestamp, 0 means ignore
     *             the timestamp
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    listOwned(criteria?: Criteria, sortOrder?: SortOrder, offset?: number, length?: number, usedAfter?: number, opts?: ApiOpts) {
        //return rqBroker.doGet({url: es.api.getListQueryURL(resourceName, criteria, sortOrder, offset, length)});
        return this.cache.doGetList(this.rqBroker, this.resourceName + "/listowned", criteria, sortOrder, offset, length, usedAfter, opts);
    }

    /**
     * Get a count of resources matching the given Criteria
     * 
     * @param criteria
     * @param opts 
     * 
     * @returns long
     */
    count(criteria?: Criteria, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/count" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");

        return this.rqBroker.doGet({url: url, opts: opts});
    }

    /**
     * Get a count of resources matching the given Criteria
     * 
     * @param criteria
     * @param opts 
     * 
     * @returns long
     */
    countOwned(criteria?: Criteria, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/countowned" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");

        return this.rqBroker.doGet({url: url, opts: opts});
    }

    /**
     * Get a count of resources matching the given Criteria
     * 
     * @param criteria
     * @param opts 
     * 
     * @returns long
     */
    countShared(criteria?: Criteria, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/countshared" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");

        return this.rqBroker.doGet({url: url, opts: opts});
    }

    /**
     * POST a new resource to the server (note: id attrib must be -1)
     * 
     * @param resourceObj
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    create(resourceObj: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }
        
        if(!resourceObj["$type"]){
            es.log.error('Missing $type');
        }

        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName),
            opts: opts,
            data: resourceObj
        }).done((rs) => {
            this.cache?.setEntity(this.resourceName, rs);
            this.cache?.clear("Security", "ownership_workspace");
            this.cache?.clear("Security", "ownership_geomap");

            this.eventChannel?.fireEvent("onCreateWorkspace", rs);
        });
    }
    
    /**
     * 
     * @param resourceObj 
     * @param opts 
     * 
     * @returns 
     */
    createWithoutGeoMap(resourceObj: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }
        
        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName + "/without_geomap"),
            opts: opts,
            data: resourceObj
        }).done((rs) => {
            this.cache?.setEntity(this.resourceName, rs);
            this.cache?.clear("Security", "ownership_workspace");

            this.eventChannel?.fireEvent("onCreateWorkspace", rs);
        });
    }
    
    /**
     * PUT a new resource to the server (note: id attrib must be valid)
     * 
     * @param resourceObj 
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    update(resourceObj: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }
        
        return this.rqBroker.doPut({
            url: es.api.getURL(this.resourceName) + "/" + resourceObj.id,
            data: resourceObj, opts: opts
        }).done((rs) => {
            this.cache?.setEntity(this.resourceName, rs);
            this.cache?.clear("GeoMap");

            this.eventChannel?.fireEvent("onUpdateWorkspace", rs);
        });
    }
    
    /**
     * Get the resource from the server
     * 
     * @param id 
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    remove(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + id;

        return this.rqBroker.doDelete({url: url, opts: opts}).done((rs) => {
            this.cache?.clearAll(); // clear all client-side cache to be safe. workspace is top object and cascades everywhere. should be relatively rare event anyway

            this.eventChannel?.fireEvent("onRemoveWorkspace", id);
        });
    }

    /**
     * Remove workspaces including geomaps.
     *
     * @param ids
     * @param opts
     * 
     * @returns list of successfully removed workspaces
     */
    removeBulk(ids: number[], opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(ids)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/remove_bulk";

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: ids
        }).done((rs) => {
            this.cache?.clearAll(); // clear all client-side cache to be safe. workspace is top object and cascades everywhere. should be relatively rare event anyway

            for (var index=0; index< rs.removed.length; index++) {
                this.eventChannel?.fireEvent("onRemoveWorkspace", rs.removed[index]);
            }
        });
    }

    /**
     * 
     * @param workspaceEntitlements 
     * @param geoMapEntitlements 
     * @param emailNotify 
     * 
     * @returns 
     */
    shareWorkspace(workspaceEntitlements: object[], geoMapEntitlements: object[], emailNotify?: boolean) {
        const workspaceId = workspaceEntitlements.id;
        const geoMapIds = geoMapEntitlements.map(gme => gme.id);

        const url = es.api.getURL("workspace_share/") + workspaceId;
        const data = {
            'workspaceEntitlements': workspaceEntitlements,
            'geoMapEntitlements': geoMapEntitlements,
            'emailNotify': !!emailNotify
        };

        return this.rqBroker.doPost({'url': url, 'data': data}).done(() => {
            this.cache?.clear('entitlements_workspace', workspaceId);
            geoMapIds.forEach((id) => {
                this.cache?.clear('entitlements_geomap', id);
            });
            this.cache?.clear('entitlements_layerview');
            this.cache?.clear("Security"); // required?

            this.eventChannel?.fireEvent("onUpdateEntitlements", "workspace", workspaceId); // TODO FIXME should fake all entitlements update?
        });
    }
}
